/**
 * SyndicationTab - Manage Hudl Fan and YouTube Channel syndication sources for automatic game scheduling
 * Games from each source are automatically assigned to the specified channel
 */
import { useState, useEffect } from "react";
import { 
  Globe, Plus, Trash2, RefreshCw, ExternalLink, X, 
  Calendar, Tv, AlertCircle, Clock, Loader2, Edit2, Check, Youtube
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SyndicationTab = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [channels, setChannels] = useState([]);
  const [sourceType, setSourceType] = useState("hudl"); // "hudl" or "youtube"
  const [validatingYoutube, setValidatingYoutube] = useState(false);
  const [youtubeChannelInfo, setYoutubeChannelInfo] = useState(null);
  
  const [newSource, setNewSource] = useState({
    name: "",
    source_type: "hudl",
    hudl_fan_url: "",
    youtube_channel_url: "",
    channel_id: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchSources();
    fetchChannels();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API}/syndication/sources`, { headers: getAuthHeaders() });
      setSources(res.data);
    } catch (err) {
      console.error("Error fetching syndication sources:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API}/channels`);
      setChannels(res.data.filter(ch => ch.name)); // Only show channels with names
    } catch (err) {
      console.error("Error fetching channels:", err);
    }
  };

  const validateYoutubeChannel = async (url) => {
    if (!url) return;
    setValidatingYoutube(true);
    setYoutubeChannelInfo(null);
    
    try {
      const res = await axios.post(`${API}/syndication/validate-youtube-channel`, { url }, { headers: getAuthHeaders() });
      setYoutubeChannelInfo(res.data);
      // Auto-fill name if empty
      if (!newSource.name && res.data.title) {
        setNewSource({ ...newSource, name: res.data.title, youtube_channel_url: url });
      }
    } catch (err) {
      setYoutubeChannelInfo({ valid: false, error: err.response?.data?.detail || "Invalid YouTube channel" });
    } finally {
      setValidatingYoutube(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name) {
      alert("Please enter a name");
      return;
    }
    
    if (sourceType === "hudl" && !newSource.hudl_fan_url) {
      alert("Please enter a Hudl Fan URL");
      return;
    }
    
    if (sourceType === "youtube" && !newSource.youtube_channel_url) {
      alert("Please enter a YouTube channel URL");
      return;
    }

    try {
      const payload = {
        name: newSource.name,
        source_type: sourceType,
        hudl_fan_url: sourceType === "hudl" ? newSource.hudl_fan_url : "",
        youtube_channel_url: sourceType === "youtube" ? newSource.youtube_channel_url : "",
        channel_id: newSource.channel_id
      };
      
      const res = await axios.post(`${API}/syndication/sources`, payload, { headers: getAuthHeaders() });
      setSources([...sources, res.data]);
      setNewSource({ name: "", source_type: "hudl", hudl_fan_url: "", youtube_channel_url: "", channel_id: "" });
      setShowAddForm(false);
      setYoutubeChannelInfo(null);
    } catch (err) {
      alert("Error adding source: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateSource = async (sourceId, updates) => {
    try {
      const res = await axios.put(`${API}/syndication/sources/${sourceId}`, updates, { headers: getAuthHeaders() });
      setSources(sources.map(s => s.id === sourceId ? { ...s, ...res.data } : s));
      setEditingSource(null);
    } catch (err) {
      alert("Error updating source: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!confirm("Are you sure you want to delete this syndication source?")) return;
    
    try {
      await axios.delete(`${API}/syndication/sources/${sourceId}`, { headers: getAuthHeaders() });
      setSources(sources.filter(s => s.id !== sourceId));
    } catch (err) {
      alert("Error deleting source: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSyncSource = async (sourceId) => {
    setSyncing(sourceId);
    try {
      const res = await axios.post(`${API}/syndication/sources/${sourceId}/sync`, {}, { headers: getAuthHeaders() });
      alert(`Synced ${res.data.games_imported || 0} games from this source`);
      fetchSources();
    } catch (err) {
      alert("Error syncing: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncing("all");
    try {
      const res = await axios.post(`${API}/syndication/sync-all`, {}, { headers: getAuthHeaders() });
      alert(`Synced ${res.data.total_games_imported || 0} games from all sources`);
      fetchSources();
    } catch (err) {
      alert("Error syncing all: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(ch => ch.channel_id === channelId);
    return channel?.name || "No channel assigned";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase text-white">Syndication</h2>
          <p className="text-zinc-500 text-sm">Auto-sync games from Hudl Fan and YouTube channels every 12 hours</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSyncAll}
            disabled={syncing === "all" || sources.length === 0}
            className="bg-zinc-700 hover:bg-zinc-600 text-white"
          >
            {syncing === "all" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync All Now
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        </div>
      </div>

      {/* Add Source Form */}
      {showAddForm && (
        <div className="bg-zinc-800/50 border border-white/10 p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white uppercase tracking-wider">Add Syndication Source</h3>
            <button onClick={() => { setShowAddForm(false); setYoutubeChannelInfo(null); }} className="text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Source Type Selector */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setSourceType("hudl"); setYoutubeChannelInfo(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                sourceType === "hudl" 
                  ? "bg-orange-500 text-black" 
                  : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              }`}
            >
              <Globe className="w-4 h-4" />
              Hudl Fan
            </button>
            <button
              onClick={() => setSourceType("youtube")}
              className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                sourceType === "youtube" 
                  ? "bg-red-600 text-white" 
                  : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              }`}
            >
              <Youtube className="w-4 h-4" />
              YouTube Channel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                School/Team Name *
              </label>
              <input
                type="text"
                value={newSource.name}
                onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                placeholder="e.g., UNT Dallas"
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="syndication-name-input"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Assign to Channel *
              </label>
              <select
                value={newSource.channel_id}
                onChange={(e) => setNewSource({ ...newSource, channel_id: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="syndication-channel-select"
              >
                <option value="">-- Select Channel --</option>
                {channels.map((ch) => (
                  <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>
                ))}
              </select>
              <p className="text-xs text-zinc-600 mt-1">
                All games from this source will be scheduled on this channel
              </p>
            </div>
          </div>

          {/* Hudl Fan URL Input */}
          {sourceType === "hudl" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Hudl Fan URL *
              </label>
              <input
                type="url"
                value={newSource.hudl_fan_url}
                onChange={(e) => setNewSource({ ...newSource, hudl_fan_url: e.target.value })}
                placeholder="https://fan.hudl.com/usa/tx/city/organization/xxxxx/school-name"
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="syndication-url-input"
              />
              <p className="text-xs text-zinc-600 mt-1">
                Go to the school's Hudl Fan page and copy the URL
              </p>
            </div>
          )}

          {/* YouTube Channel URL Input */}
          {sourceType === "youtube" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                YouTube Channel URL *
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newSource.youtube_channel_url}
                  onChange={(e) => setNewSource({ ...newSource, youtube_channel_url: e.target.value })}
                  onBlur={(e) => validateYoutubeChannel(e.target.value)}
                  placeholder="https://www.youtube.com/@ChannelName or youtube.com/channel/UC..."
                  className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  data-testid="syndication-youtube-url-input"
                />
                <Button
                  onClick={() => validateYoutubeChannel(newSource.youtube_channel_url)}
                  disabled={validatingYoutube || !newSource.youtube_channel_url}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white"
                >
                  {validatingYoutube ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}
                </Button>
              </div>
              <p className="text-xs text-zinc-600 mt-1">
                Supports @handle, /channel/UC..., /c/Name, or /user/ URLs
              </p>
              
              {/* YouTube Channel Preview */}
              {youtubeChannelInfo && (
                <div className={`mt-3 p-3 rounded ${youtubeChannelInfo.valid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  {youtubeChannelInfo.valid ? (
                    <div className="flex items-center gap-3">
                      {youtubeChannelInfo.thumbnail_url && (
                        <img src={youtubeChannelInfo.thumbnail_url} alt="" className="w-12 h-12 rounded-full" />
                      )}
                      <div>
                        <p className="text-white font-bold">{youtubeChannelInfo.title}</p>
                        <p className="text-green-400 text-xs flex items-center gap-1">
                          <Check className="w-3 h-3" /> Channel verified
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {youtubeChannelInfo.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              onClick={() => { setShowAddForm(false); setYoutubeChannelInfo(null); }}
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSource}
              disabled={sourceType === "youtube" && youtubeChannelInfo && !youtubeChannelInfo.valid}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Source
            </Button>
          </div>
        </div>
      )}

      {/* Sources List */}
      {sources.length === 0 ? (
        <div className="bg-zinc-800/50 border border-white/10 p-8 text-center">
          <Globe className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">No Syndication Sources</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Add a Hudl Fan or YouTube channel source to automatically import games
          </p>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Source
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map((source) => (
            <div 
              key={source.id}
              className="bg-zinc-800/50 border border-white/10 p-4"
            >
              {editingSource === source.id ? (
                <EditSourceForm 
                  source={source} 
                  channels={channels}
                  onSave={(updates) => handleUpdateSource(source.id, updates)}
                  onCancel={() => setEditingSource(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      source.source_type === "youtube" ? "bg-red-600" : "bg-zinc-700"
                    }`}>
                      {source.source_type === "youtube" ? (
                        <Youtube className="w-6 h-6 text-white" />
                      ) : (
                        <Globe className="w-6 h-6 text-orange-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-bold">{source.name}</h4>
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                          source.source_type === "youtube" 
                            ? "bg-red-500/20 text-red-400" 
                            : "bg-orange-500/20 text-orange-400"
                        }`}>
                          {source.source_type === "youtube" ? "YouTube" : "Hudl"}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-xs truncate max-w-md">
                        {source.source_type === "youtube" 
                          ? source.youtube_channel_url 
                          : source.hudl_fan_url}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs">
                        <span className={`flex items-center gap-1 ${source.channel_id ? 'text-green-400' : 'text-yellow-400'}`}>
                          <Tv className="w-3 h-3" />
                          {source.channel_id ? getChannelName(source.channel_id) : "No channel assigned"}
                        </span>
                        <span className="text-zinc-600 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {source.games_count || 0} games
                        </span>
                        <span className="text-zinc-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last sync: {formatDate(source.last_sync)}
                        </span>
                      </div>
                      {source.last_sync_result && (
                        <p className={`text-xs mt-1 ${source.last_sync_result.startsWith('Error') ? 'text-red-400' : 'text-green-400'}`}>
                          {source.last_sync_result}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <a
                      href={source.hudl_fan_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-zinc-500 hover:text-white transition-colors"
                      title="Open Hudl Fan page"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    
                    <Button
                      onClick={() => setEditingSource(source.id)}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white"
                      size="sm"
                      title="Edit source"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      onClick={() => handleSyncSource(source.id)}
                      disabled={syncing === source.id}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white"
                      size="sm"
                      title="Sync now"
                    >
                      {syncing === source.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <Button
                      onClick={() => handleDeleteSource(source.id)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                      size="sm"
                      title="Delete source"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-zinc-900/50 border border-white/10 p-4">
        <h4 className="text-white font-bold text-sm uppercase mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          How It Works
        </h4>
        <ol className="text-zinc-400 text-sm space-y-2 list-decimal list-inside">
          <li>Go to <a href="https://fan.hudl.com" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">fan.hudl.com</a> and find the school's page</li>
          <li>Copy the full URL from your browser's address bar</li>
          <li>Add the source above and assign it to a channel</li>
          <li>Games are automatically synced every 12 hours, or click "Sync" to import now</li>
        </ol>
        <p className="text-zinc-500 text-xs mt-3 border-t border-white/5 pt-3">
          <strong>Note:</strong> Games are imported with VCloud embed URLs and will display on their assigned channel.
          The sport type is automatically detected from the game title.
        </p>
      </div>
    </div>
  );
};

// Edit form component for inline editing
const EditSourceForm = ({ source, channels, onSave, onCancel }) => {
  const [name, setName] = useState(source.name);
  const [channelId, setChannelId] = useState(source.channel_id || "");

  const handleSave = () => {
    onSave({ name, channel_id: channelId });
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex-1 grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Channel</label>
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">-- No Channel --</option>
            {channels.map((ch) => (
              <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} className="bg-green-500 hover:bg-green-600 text-white" size="sm">
          <Check className="w-4 h-4" />
        </Button>
        <Button onClick={onCancel} className="bg-zinc-700 hover:bg-zinc-600 text-white" size="sm">
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default SyndicationTab;
